import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";
import { getValueOrDefault } from "Util/shipeezi.table";

import Modal from "../../../../../../../../../components/Generic/Modal";
import { Fleet } from "../../../../../../../../../models/fleet";
import { Filters, Searches } from "../../../../../../../../../models/shipeezi.table";
import { EquipmentOwnership, EquipmentType, Status } from "../../../../../../../../../util/enums";
import { useSubcontractorActions } from "../../../hooks/useActions";
import useStyles, { Content } from "./styles";

export interface AddEquipmentModalProps {
  isOpened: boolean;
  onClose: () => void;
  equipmentsIdAdded: Array<string>;
  onConfirmAddEquipments: (members: Array<any>) => void;
}

const AddEquipmentModal: React.FC<AddEquipmentModalProps> = ({
  isOpened,
  onClose,
  equipmentsIdAdded,
  onConfirmAddEquipments,
}) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [equipments, setEquipments] = useState<Fleet[]>([]);
  const [selectedEquipments, setSelectedEquipments] = useState<Array<Fleet>>([]);

  const getSelectedEquipmentsId = () => {
    const ids = selectedEquipments.map((fleet) => String(fleet.id));

    if (equipmentsIdAdded?.length) return ids.concat(equipmentsIdAdded);

    return ids;
  };

  const handleSelect = (idsSelected: number[]) => {
    const selecteds = [...selectedEquipments, ...equipments].filter((fleet) =>
      idsSelected.includes(fleet.id!)
    );

    setSelectedEquipments(selecteds);
  };

  const [page, setPage] = useState<number>(0);
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { getAllEquipmentsForSubcontractor, getCountEquipmentsForSubcontractor } =
    useSubcontractorActions();

  const { data: equipmentsData, isFetching: isFetchingData } = getAllEquipmentsForSubcontractor(
    page,
    pageSize,
    searches,
    filters
  );
  const { data: equipmentsCount, isFetching: isFetchingCount } = getCountEquipmentsForSubcontractor(
    filters,
    searches
  );

  const onCloseModal = () => {
    setSelectedEquipments([]);

    setFilters(undefined);
    setSearches([]);
    onClose();
  };

  useEffect(() => {
    if (equipmentsData) {
      setEquipments(equipmentsData);
    }
  }, [equipmentsData]);

  useEffect(() => {
    if (equipmentsCount) {
      setTotal(equipmentsCount);
    }
  }, [equipmentsCount]);

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const columns: Column[] = [
    {
      name: "equipmentType",
      title: translate("type"),
      formatText: (value: Fleet["equipmentType"]) => value ?? "-",
      hideSearch: true,
      hideSort: true,
    },
    // {
    //   name: "fleetType",
    //   title: translate("type"),
    //   formatText: (value: Fleet["fleetType"]) => value?.name ?? "-",
    //   hideSearch: true,
    //   hideSort: true,
    // },
    {
      name: "description",
      title: translate("description"),
      formatText: (value: Fleet["description"]) => value ?? "-",
    },
    {
      name: "ownership",
      title: translate("belongsTo"),
      hideSearch: true,
      formatText: (value) => getValueOrDefault(value),
    },
    {
      name: "condition",
      title: translate("condition"),
      formatText: (value: Fleet["condition"]) => value ?? "-",
      hideSearch: true,
    },
    {
      name: "status",
      title: "Status",
      hideSearch: true,
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "equipmentType",
          options: [EquipmentType.FLEET, EquipmentType.GENERAL],
        },
        {
          name: translate("belongsTo"),
          column: "ownership",
          options: [
            EquipmentOwnership.OWN,
            EquipmentOwnership.RENTAL,
            EquipmentOwnership.SUBCONTRACTOR,
            EquipmentOwnership.LEASED,
          ],
        },
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
      ],
      onChange: handleFilter,
    },
  };

  const onSubmit = () => {
    onConfirmAddEquipments(selectedEquipments);
    onCloseModal();
  };

  return (
    <div>
      <Modal
        open={isOpened}
        handleClose={onClose}
        title={translate("addAEquipment")}
        showActionButtons
        actionButtonProps={{
          onSave: onSubmit,
          onCancel: onCloseModal,
          textSave: translate("addEquipments"),
        }}
      >
        <Content>
          <ShipeeziNewGrid
            data={equipments}
            totalItems={total}
            columns={columns}
            loadingData={isFetchingData || isFetchingCount}
            emptyData={translate("noEquipmentToShow")}
            selecteds={getSelectedEquipmentsId()}
            handleSelect={handleSelect}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            onChangePageSize={setPageSize}
            onNextPage={setCurrentPage}
            onPreviousPage={setCurrentPage}
            onGoToPage={setCurrentPage}
            positionActionsColumn="right"
            generalActions={generalActions}
            showSort={false}
          />
        </Content>
      </Modal>
    </div>
  );
};

export default AddEquipmentModal;
